import React, {useState} from 'react'
import {getPriceForCountry} from '../../utils/getPriceForCountry'
import {ProductGroupCard} from '../ProductGroupCard'
import {CARD_VARIANTS} from '../../utils/cardVariants'

// Builds the product payload for both the "add" and stepper
// increment/decrement flows — isQuantityChange scales price by quantity
// (stepper addons are priced per unit), a plain add is always unit price.
export function buildProduct(
  plan,
  group,
  selectedCountry,
  quantity = 1,
  isQuantityChange = false,
) {
  // Checked on the plan's own prod_type, not the group's — "Thryv Add-Ons"
  // mixes Apps and OneTimeFees (e.g. Professional Designed Logo) under one
  // group, so group.prod_type alone can't tell them apart.
  const isOneTimeFee = plan.prod_type === 'OneTimeFees'
  const basePrice = getPriceForCountry(plan.currencies, selectedCountry)
  const computedPrice = isQuantityChange ? basePrice * quantity : basePrice
  return {
    productId: plan.code,
    name: plan.name,
    price: computedPrice,
    type: isOneTimeFee
      ? 'onetime'
      : plan.prod_type === 'Center'
      ? 'software'
      : 'addon',
    billingFrequency: isOneTimeFee
      ? null
      : plan.productBillingType === 'Annual'
      ? 'annual'
      : 'monthly',
    quantity,
    configuredOptions: {},
    excludes: plan.excludes ?? [],
    autoAddCenters: plan.autoAddCenters ?? [],
    maxQty: plan.maxQty ?? null,
    parentProduct: plan.parentProduct,
  }
}

// Owns increment/decrement, min/max quantity, and the upgrade-scenario floor
// for a "direct stepper" addon card (see getStepperConfig in
// planFieldsConfig.js) — isolated from AddonsGrid's per-variant rendering.
//
// floor is captured once at mount from the selectedProduct snapshot: if this
// addon is already account-owned (alreadyIncluded, from an upgrade), the
// customer can't decrement below that starting quantity. It has to be
// captured once rather than read live, because each quantity change
// dispatches immediately (via buildProduct, which doesn't carry
// alreadyIncluded forward) — so the flag on the live item disappears after
// the very first click.
export function DirectStepperCard({
  plan,
  group,
  groupPlanCodes,
  selectedCountry,
  selectedProduct,
  stepperConfig,
  isSelected,
  isDimmed,
  onSelect,
  onAddProduct,
  onQuantityChange,
}) {
  const quantity = selectedProduct?.quantity ?? 0

  const [floor] = useState(() =>
    selectedProduct?.alreadyIncluded ? quantity : 0,
  )

  const minQuantity = Math.max(stepperConfig.min ?? 0, floor)
  const maxQuantity =
    stepperConfig.max ?? (plan?.maxQty ? Number(plan.maxQty) : null)
  const basePrice = getPriceForCountry(plan.currencies, selectedCountry)
  const displayPrice = basePrice * quantity
  const showPrice = minQuantity === 0 ? quantity > 0 : true
  // 5 Extra Website Pages bills by the pack (quantity), but shows the
  // actual page count (quantity * 5) — price stays tied to quantity/packs.
  const displayMultiplier = stepperConfig.displayMultiplier ?? 1
  const displayQuantity =
    displayMultiplier !== 1 ? quantity * displayMultiplier : undefined

  const handleIncrement = () => {
    if (maxQuantity != null && quantity >= maxQuantity) return
    const nextQuantity = quantity + 1
    const product = buildProduct(
      plan,
      group,
      selectedCountry,
      nextQuantity,
      true,
    )
    if (!selectedProduct) {
      onAddProduct(product, groupPlanCodes, group.id)
      return
    }
    onQuantityChange?.(product, nextQuantity, group.id)
  }

  // Typed quantity. The stepper has already clamped to [minQuantity,
  // maxQuantity], so this only has to pick add-vs-update like handleIncrement.
  const handleQuantityInput = nextQuantity => {
    if (nextQuantity === quantity) return
    const product = buildProduct(
      plan,
      group,
      selectedCountry,
      nextQuantity,
      true,
    )
    if (!selectedProduct) {
      if (nextQuantity <= 0) return
      onAddProduct(product, groupPlanCodes, group.id)
      return
    }
    onQuantityChange?.(product, nextQuantity, group.id)
  }

  const handleDecrement = () => {
    if (quantity <= minQuantity) return
    const nextQuantity = quantity - 1
    onQuantityChange?.(
      buildProduct(plan, group, selectedCountry, nextQuantity, true),
      nextQuantity,
      group.id,
    )
  }

  return (
    <ProductGroupCard
      name={plan.name}
      description={plan.description}
      price={displayPrice}
      showPrice={showPrice}
      selectedCountry={selectedCountry}
      billingPeriod={
        plan.prod_type === 'OneTimeFees'
          ? 'one_time'
          : plan.productBillingType === 'Annual'
          ? 'annual'
          : 'monthly'
      }
      isSelected={isSelected}
      isAdded={quantity > 0}
      isDimmed={isDimmed}
      cardVariant={CARD_VARIANTS.DIRECT_STEPPER}
      quantity={quantity}
      minQuantity={minQuantity}
      maxQuantity={maxQuantity}
      displayQuantity={displayQuantity}
      displayMultiplier={displayMultiplier}
      onSelect={onSelect}
      onIncrement={handleIncrement}
      onDecrement={handleDecrement}
      onQuantityInput={handleQuantityInput}
    />
  )
}
